import React, { useState } from 'react';
import { ChevronUp, ChevronDown, Clock, Package } from 'lucide-react';
import { PackageData, PackageGeometry, Product, ProductMeta, Shipment } from '../models/DeliveryOptionDetailsModel';
import ProductLeadTimeSection from './ProductLeadTimeSection';

interface PackagingSectionProps {
  packageGeometry: PackageGeometry | undefined;
  shipment: Shipment;
}

/** --- Dynamic Package Visualizer --- */

const PackageVisualizer = ({ l, b, h, shape }: { l: number, b: number, h: number, shape: string }) => {
  const max = Math.max(l, b, h);
  const ratio = 60 / max;
  const L = l * ratio; 
  const B = b * ratio; 
  const H = h * ratio;

  // Cardboard Color Palette
  const kraftLight = "#e3c5a8";
  const kraftMedium = "#d2a679";
  const kraftDark = "#b38b59";

  return (
    <div className="w-full h-full flex items-center justify-center p-4">
      {/* Increased viewBox from 120x100 to 200x150 to provide 
          padding for 4+ digit text strings.
      */}
      <svg width="180" height="150" viewBox="0 0 200 150" preserveAspectRatio="xMidYMid meet">
        {/* Shifted center point to 100, 100 to account for new viewBox */}
        <g transform="translate(100, 100)">
          {shape === "TriangularTube" ? (
            <>
              <path d={`M 0 0 L ${-B} 0 L ${-B / 2} ${-H} Z`} fill={kraftMedium} stroke="#000000" strokeWidth="2" />
              <path d={`M 0 0 L ${L} ${-L / 2} L ${L - B / 2} ${-L / 2 - H} L ${-B / 2} ${-H} Z`} fill={kraftDark} stroke="#000000" strokeWidth="2" />
              <line x1={-B / 2} y1={-H} x2={L - B / 2} y2={-L / 2 - H} stroke="#000000" strokeWidth="2" />

              <text x={L / 2 + 8} y={-L / 4 + 10} fill="#000000" fontSize="10" fontWeight="900" textAnchor="start">L:{l}</text>
              <text x={-B / 2} y={15} fill="#000000" fontSize="10" fontWeight="900" textAnchor="middle">W:{b}</text>
              <text x={-B - 8} y={-H / 2} fill="#000000" fontSize="10" fontWeight="900" textAnchor="end">H:{h}</text>
            </>
          ) : shape === "Envelope" ? (
            <>
              <rect x={-B / 2} y={-L} width={B} height={L} fill="#fef3c7" stroke="#000000" strokeWidth="2" rx="2" />
              <path d={`M ${-B / 2} ${-L} L 0 ${-L + (L * 0.4)} L ${B / 2} ${-L}`} fill="#fde68a" stroke="#000000" strokeWidth="1.5" />
              <path d={`M ${-B / 2} 0 L 0 ${-L * 0.4} L ${B / 2} 0`} fill="none" stroke="#d97706" strokeWidth="1" strokeDasharray="2" strokeOpacity="0.4" />

              <text x={B / 2 + 10} y={-L / 2} fill="#000000" fontSize="10" fontWeight="900" textAnchor="start">L:{l}</text>
              <text x="0" y={20} fill="#000000" fontSize="10" fontWeight="900" textAnchor="middle">W:{b}</text>
              <text x={-B / 2 - 10} y={-L / 2} fill="#000000" fontSize="9" fontWeight="900" textAnchor="end">H:{h}</text>
            </>
          ) : (
            <>
              <path d={`M 0 0 L ${L} ${-L / 2} L ${L} ${-L / 2 - H} L 0 ${-H} Z`} fill={kraftMedium} stroke="#000000" strokeWidth="2" />
              <path d={`M 0 0 L ${-B} ${-B / 2} L ${-B} ${-B / 2 - H} L 0 ${-H} Z`} fill={kraftDark} stroke="#000000" strokeWidth="2" />
              <path d={`M 0 ${-H} L ${L} ${-L / 2 - H} L ${L - B} ${-(L + B) / 2 - H} L ${-B} ${-B / 2 - H} Z`} fill={kraftLight} stroke="#000000" strokeWidth="2" />

              <text x={L / 2 + 10} y={-L / 4 + 8} fill="#000000" fontSize="11" fontWeight="900" textAnchor="start">L:{l}</text>
              <text x={-B / 2 - 10} y={-B / 4 + 6} fill="#000000" fontSize="11" fontWeight="900" textAnchor="end">W:{b}</text>
              <text x="0" y={-H - 15} fill="#000000" fontSize="11" fontWeight="900" textAnchor="middle">H:{h}</text>
            </>
          )}
        </g>
      </svg>
    </div>
  );
};

/** --- SVG Weight Block --- */
const WeightVisualizer = ({ weight }: { weight: number }) => {
  const isHeavy = weight > 20000;
  return (
    <div className="w-full h-full flex items-center justify-center p-2">
      <svg width="85" height="90" viewBox="0 0 80 90">
        <circle cx="40" cy="25" r="14" fill="none" stroke="#000000" strokeWidth="6" />
        <path d="M 15 35 L 65 35 L 75 80 L 5 80 Z" fill={isHeavy ? "#ef4444" : "#cbd5e1"} stroke="#000000" strokeWidth="3" />
        <text x="40" y="62" fill={isHeavy ? "#ffffff" : "#000000"} fontSize="16" fontWeight="900" textAnchor="middle">{weight}g</text>
      </svg>
    </div>
  );
};

const ProductItem = ({ tag, product, quantity }: { tag: string, product: Product, quantity?: number }) => {
  const [isOpen, setIsOpen] = useState(false);
  const meta: ProductMeta = product.ProductMeta;
  const bundleData: PackageData = product.PackageData;
  const articleCode = meta?.ArticleCode || "N/A";
  const description = meta?.Description || "N/A";
  const options = meta?.Options || {};
  const entries = Object.entries(options);

  return (
    <div className="bg-white border border-slate-300 rounded-lg overflow-hidden mb-1 shadow-sm text-black"
      style={{ padding: '5px', paddingLeft: '10px' }}>
      <div className="p-1.5 cursor-pointer hover:bg-slate-50 flex items-center" onClick={() => setIsOpen(!isOpen)}>
        <div className="flex items-center shrink-0 gap-2 w-full justify-between" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
            {quantity !== undefined && (
              <span className="bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded text-[9px] font-black leading-none">
                {quantity} x
              </span>
            )}
            <span className="text-[11px] font-mono font-bold text-slate-500 lowercase">{tag}</span>
            <span className="text-[10px] font-mono font-black text-teal-600 opacity-70">
              {description}
            </span>
            <Clock size={12} className="text-blue-600 shrink-0" />
            <span className="text-[10px] font-mono font-black text-blue-600 opacity-70">
              [{product.ProductLeadTime?.LeadTimeBreakdown?.Total ?? "N/A"} days]
            </span>
          </div>
          {isOpen ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
        </div>
      </div>
      {isOpen && (
        <div className="p-3 bg-slate-50 border-t border-slate-200">
          <table className="w-full table-fixed border-collapse" width="100%">
            <tbody>
              <tr>
                <td style={{ width: '50%' }} className="align-top border-r border-slate-200 pr-4">
                  <div className="text-[10px] font-black text-slate-700 uppercase mb-2 border-b border-slate-300 pb-0.5">Options</div>
                  <table className="w-full border-separate border-spacing-y-1">
                    <tbody>
                      {entries.map(([k, v]) => (
                        <tr key={k} className="align-top">
                          <td className="w-[60px] text-[9px] text-slate-500 font-bold uppercase pr-1">{k}</td>
                          <td className="w-[5px] text-[9px] text-slate-400 font-bold">:</td>
                          <td className="text-[10px] font-black text-black pl-1 break-all lowercase leading-tight">{String(v)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </td>
                <td style={{ width: '50%' }} className="align-top pl-4 text-black">
                  <div className="text-[10px] font-black text-slate-700 uppercase mb-2 border-b border-slate-300 pb-0.5">Physical Profile</div>
                  {bundleData ? (
                    <table className="w-full border-separate border-spacing-y-1">
                      <tbody>
                        <tr className="align-top">
                          <td className="w-[70px] text-[9px] text-slate-500 font-bold uppercase pr-1">Dimension</td>
                          <td className="w-[5px] text-[9px] text-slate-400 font-bold">:</td>
                          <td className="text-[10px] font-mono font-black text-black pl-1">
                            {bundleData.Dimensions.LengthMm}x{bundleData.Dimensions.BreadthMm}x{bundleData.Dimensions.HeightMm}mm
                          </td>
                        </tr>
                        <tr className="align-top">
                          <td className="w-[70px] text-[9px] text-slate-500 font-bold uppercase pr-1">Weight</td>
                          <td className="w-[5px] text-[9px] text-slate-400 font-bold">:</td>
                          <td className="text-[10px] font-mono font-black text-black pl-1">{bundleData.Dimensions.WeightGr}g</td>
                        </tr>
                        <tr className="align-top">
                          <td className="w-[70px] text-[9px] text-slate-500 font-bold uppercase pr-1">Modifiers</td>
                          <td className="w-[5px] text-[9px] text-slate-400 font-bold">:</td>
                          <td className="pl-1">
                            <div className="max-h-[100px] overflow-y-auto custom-scrollbar flex flex-col gap-0.5 pr-1">
                              {bundleData.AppliedModifiers?.map((m: string, idx: number) => (
                                <div key={idx} className="text-[9px] font-bold text-slate-600 lowercase leading-tight">
                                  • {m}
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  ) : (
                    <div className="text-[10px] italic text-slate-400 pt-2 pl-1">No bundle data</div>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
          <ProductLeadTimeSection product={product} />
        </div>
      )}
    </div>
  );
};

const PackagingSection: React.FC<PackagingSectionProps> = ({
  packageGeometry, shipment
}) => {
  const [openStates, setOpenStates] = useState<Record<string, boolean>>({});
  const toggle = (id: string) => setOpenStates(prev => ({ ...prev, [id]: !prev[id] }));
  if (!packageGeometry) return null;
  return (<div className="bg-white border-2 border-teal-300 rounded-lg overflow-hidden shadow-sm">
    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: '10px', cursor: 'pointer', whiteSpace: 'nowrap' }} className="bg-teal-100/30 hover:bg-teal-100" onClick={() => toggle('pkg')}>
      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
        <Package size={14} className="text-teal-900 shrink-0" />
        <span className="text-xs font-black text-teal-950 uppercase shrink-0">Packaging</span>
        <span className="text-[10px] font-mono font-black text-teal-600 opacity-70">[{packageGeometry.LengthMm}x{packageGeometry.BreadthMm}x{packageGeometry.HeightMm}mm / {packageGeometry.WeightGrams}g]</span>
      </div>
      {openStates['pkg'] ? <ChevronUp size={14} className="text-teal-900" /> : <ChevronDown size={14} className="text-teal-900" />}
    </div>
    {openStates['pkg'] && (
      <div className="border-t-2 border-teal-200 flex flex-col">
        <div className="p-2 border-t-2 border-teal-200 bg-white flex justify-between items-center text-[11px] font-black tracking-tight uppercase text-black">
          <span>{"name: "}{packageGeometry.PackageName} ({packageGeometry.Shape})</span>
        </div>
        <table className="w-full border-collapse table-fixed h-[140px]"><tbody><tr className="border-none">
          <td className="w-1/2 bg-slate-200 border-r-2 border-teal-200 p-0 align-middle"><PackageVisualizer l={packageGeometry.LengthMm} b={packageGeometry.BreadthMm} h={packageGeometry.HeightMm} shape={packageGeometry.Shape} /></td>
          <td className="w-1/2 bg-white p-0 align-middle"><WeightVisualizer weight={packageGeometry.WeightGrams} /></td>
        </tr></tbody></table>
      </div>
    )}
    <div className="bg-white p-2 rounded-lg border-2 border-teal-200 space-y-1 text-black">
      {shipment.Products.map((p: Product) => (<ProductItem key={p.Tag} tag={p.Tag} product={p} quantity={p.Quantity} />))}
    </div>
  </div>
  );
};



export default PackagingSection;